import React, { useState } from 'react';
import { Input, Dropdown, TextArea, Form, Checkbox, Icon, Popup } from 'semantic-ui-react';
import _ from 'lodash';
import V2Editor from '../../../components/SimpleCodeEditor';
import { JsonInput } from './fields';

// Şemadan (server lib/liveOptionSchema + option_schema) çizilen ayar sekmesi.
// value === undefined → alan dokümanda yok (varsayılan geçerli); onChange(undefined) alanı siler.

// Türkçe karakter ve büyük/küçük harf farkını yok say: "İnstagram" = "instagram" = "ınstagram"
export function norm(text) {
	return String(text === undefined || text === null ? '' : text)
		.replace(/[İIı]/g, 'i')
		.toLowerCase()
		.replace(/ş/g, 's')
		.replace(/ğ/g, 'g')
		.replace(/ü/g, 'u')
		.replace(/ö/g, 'o')
		.replace(/ç/g, 'c');
}

// alan adı/anahtar/açıklama/eski açıklama/bölüm; withValue ise alanın değeri de aranır
export function fieldMatch(f, q, value) {
	if (!q) return true;
	if (norm([f.label, f.path, f.desc, f.alias, f.section].join(' ')).indexOf(q) !== -1) return 'meta';
	if (value !== undefined && !f.secret && norm(typeof value === 'object' ? JSON.stringify(value) : value).indexOf(q) !== -1) return 'value';
	return false;
}

function withValueOptions(options, values) {
	let opts = _.map(options, (o) => ({ key: String(o.value), value: o.value, text: o.label || String(o.value) }));
	_.each(_.castArray(values === undefined ? [] : values), (v) => {
		if (v !== '' && v !== null && !_.find(opts, { value: v })) opts.push({ key: 'x' + v, value: v, text: String(v) });
	});
	return opts;
}

function ColorInput({ value, onChange }) {
	return (
		<div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
			<input
				type="color"
				value={/^#[0-9a-f]{6}$/i.test(value || '') ? value : '#ffffff'}
				onChange={(e) => onChange(e.target.value)}
				style={{ width: 38, height: 34, border: '1px solid #d1d5db', borderRadius: 8, padding: 2, background: '#fff' }}
			/>
			<Input value={value || ''} placeholder="#000000" onChange={(e, d) => onChange(d.value || undefined)} style={{ width: 130 }} />
		</div>
	);
}

function SecretInput({ value, onChange, placeholder }) {
	const [show, setShow] = useState(false);
	return (
		<Input
			type={show ? 'text' : 'password'}
			value={value === undefined ? '' : value}
			placeholder={placeholder}
			onChange={(e, d) => onChange(d.value === '' ? undefined : d.value)}
			icon={<Icon name={show ? 'eye slash' : 'eye'} link onClick={() => setShow(!show)} />}
			fluid
		/>
	);
}

function CodeInput({ value, onChange, lang }) {
	return (
		<div style={{ height: 200, border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
			<V2Editor value={value || ''} lang={lang} onValueChange={(code) => onChange(code ? code : undefined)} />
		</div>
	);
}

function CellInput({ col, value, onChange }) {
	switch (col.type) {
		case 'bool':
			return <Checkbox checked={!!value} onChange={(e, d) => onChange(d.checked || undefined)} style={{ marginTop: 8 }} />;
		case 'number':
			return <Input size="small" type="number" value={value === undefined ? '' : value} onChange={(e, d) => onChange(d.value === '' ? undefined : Number(d.value))} />;
		case 'enum':
			return (
				<Dropdown selection compact clearable value={value === undefined ? '' : value} options={withValueOptions(col.options, value)} onChange={(e, d) => onChange(d.value === '' ? undefined : d.value)} />
			);
		case 'color':
			return <ColorInput value={value} onChange={onChange} />;
		case 'textarea':
			return (
				<Form>
					<TextArea rows={2} value={value || ''} onChange={(e, d) => onChange(d.value || undefined)} />
				</Form>
			);
		case 'json':
			return <JsonInput value={value} rows={2} onChange={onChange} />;
		default:
			return <Input size="small" value={value === undefined ? '' : value} onChange={(e, d) => onChange(d.value === '' ? undefined : d.value)} />;
	}
}

// Nesne listesi: sadece tanımlı sütunlar düzenlenir, satırdaki diğer anahtarlar korunur.
function ListEditor({ columns, value, onChange, readonly }) {
	if (value !== undefined && !_.isArray(value)) return <JsonInput value={value} onChange={onChange} rows={6} />;
	let rows = value || [];
	let set = (next) => onChange(next.length ? next : undefined);
	let setCell = (i, key, v) => set(_.map(rows, (r, j) => (j === i ? _.omitBy(_.assign({}, r, { [key]: v }), _.isUndefined) : r)));
	let move = (i, d) => {
		let next = rows.slice();
		let [it] = next.splice(i, 1);
		next.splice(i + d, 0, it);
		set(next);
	};
	return (
		<div>
			{rows.length ? (
				<table className="yta-listtable">
					<thead>
						<tr>
							{_.map(columns, (c) => (
								<th key={c.key}>{c.label}</th>
							))}
							<th style={{ width: 92 }} />
						</tr>
					</thead>
					<tbody>
						{_.map(rows, (row, i) => (
							<tr key={i}>
								{_.map(columns, (c) => (
									<td key={c.key}>
										<CellInput col={c} value={row && row[c.key]} onChange={(v) => !readonly && setCell(i, c.key, v)} />
									</td>
								))}
								<td style={{ whiteSpace: 'nowrap' }}>
									<button className="yta-iconbtn" disabled={!i} onClick={() => move(i, -1)} title="Yukarı">
										<Icon name="arrow up" />
									</button>
									<button className="yta-iconbtn" disabled={i === rows.length - 1} onClick={() => move(i, 1)} title="Aşağı">
										<Icon name="arrow down" />
									</button>
									<button className="yta-iconbtn" onClick={() => set(_.filter(rows, (r, j) => j !== i))} title="Sil">
										<Icon name="trash alternate outline" />
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			) : null}
			{!readonly ? (
				<button className="yta-add" style={{ marginTop: 6 }} onClick={() => onChange(rows.concat([{}]))}>
					<Icon name="plus" /> Satır ekle
				</button>
			) : null}
		</div>
	);
}

export function FieldInput({ field, value, onChange }) {
	let ro = !!field.readonly;
	let change = ro ? () => {} : onChange;
	switch (field.type) {
		case 'bool':
			return (
				<Checkbox
					toggle
					disabled={ro}
					checked={value === undefined ? !!field.default : !!value}
					onChange={(e, d) => change(d.checked)}
					label={value === undefined ? 'varsayılan' : ''}
					style={{ marginTop: 6, opacity: value === undefined ? 0.6 : 1 }}
				/>
			);
		case 'number':
			return (
				<Input
					type="number"
					disabled={ro}
					value={value === undefined ? '' : value}
					placeholder={field.default === undefined ? '' : String(field.default)}
					onChange={(e, d) => change(d.value === '' ? undefined : Number(d.value))}
					style={{ width: 200 }}
				/>
			);
		case 'textarea':
			return (
				<Form>
					<TextArea disabled={ro} rows={2} value={value || ''} placeholder={field.default} onChange={(e, d) => change(d.value === '' ? undefined : d.value)} />
				</Form>
			);
		case 'enum':
			return (
				<Dropdown
					selection
					clearable
					disabled={ro}
					value={value === undefined ? '' : value}
					placeholder={field.default === undefined ? 'Seçilmedi' : 'Varsayılan: ' + (_.get(_.find(field.options, { value: field.default }), 'label') || field.default)}
					options={withValueOptions(field.options, value)}
					onChange={(e, d) => change(d.value === '' ? undefined : d.value)}
				/>
			);
		case 'color':
			return <ColorInput value={value} onChange={change} />;
		case 'strings': {
			let list = _.isArray(value) ? value : value === undefined ? [] : [value];
			return (
				<Dropdown
					selection
					multiple
					search
					allowAdditions
					fluid
					disabled={ro}
					value={list}
					additionLabel="Ekle: "
					noResultsMessage="Yazıp Enter'a basın"
					options={withValueOptions([], list)}
					onChange={(e, d) => change(d.value.length ? d.value : undefined)}
				/>
			);
		}
		case 'json':
			return <JsonInput key={field.path} value={value} rows={_.isObject(value) ? 6 : 2} onChange={change} />;
		case 'code':
			return <CodeInput value={value} lang={field.lang} onChange={change} />;
		case 'list':
			return <ListEditor columns={field.columns} value={value} onChange={change} readonly={ro} />;
		default:
			if (field.secret) return <SecretInput value={value} onChange={change} placeholder={field.default} />;
			return (
				<Input
					fluid
					disabled={ro}
					value={value === undefined ? '' : typeof value === 'object' ? JSON.stringify(value) : value}
					placeholder={field.default === undefined ? '' : String(field.default)}
					onChange={(e, d) => change(d.value === '' ? undefined : d.value)}
				/>
			);
	}
}

export function FieldRow({ field, value, isSet, changed, onChange, matchedValue }) {
	return (
		<div className={'yta-field' + (changed ? ' changed' : '')}>
			<div>
				<div className="yta-field-label">
					{field.label}
					{field.danger ? <span className="yta-badge danger">kod çalıştırır</span> : null}
					{field.secret ? <span className="yta-badge warn">gizli</span> : null}
					{field.readonly ? <span className="yta-badge">salt okunur</span> : null}
				</div>
				<div className="yta-field-key">{field.path}</div>
				{field.alias ? <div className="yta-field-desc">{field.alias}</div> : null}
				{field.desc ? <div className="yta-field-desc">{field.desc}</div> : null}
				{matchedValue ? <span className="yta-badge accent">değerde geçiyor</span> : null}
			</div>
			<div style={{ minWidth: 0 }}>
				{field.secret && field.type === 'json' && !changed ? (
					<SecretJson value={value} onChange={onChange} />
				) : (
					<FieldInput field={field} value={value} onChange={onChange} />
				)}
			</div>
			<div className="yta-field-side">
				<Popup
					size="mini"
					content={changed ? 'Kaydedilmedi' : isSet ? 'Hesapta tanımlı' : 'Tanımlı değil, varsayılan geçerli'}
					trigger={<span className={'yta-dot' + (changed ? ' changed' : isSet ? ' set' : '')} style={{ marginTop: 6 }} />}
				/>
				{(isSet || changed) && !field.readonly ? (
					<Popup
						size="mini"
						content="Alanı sil (varsayılana dön)"
						trigger={
							<button className="yta-iconbtn" onClick={() => onChange(undefined)}>
								<Icon name="undo" />
							</button>
						}
					/>
				) : null}
			</div>
		</div>
	);
}

function SecretJson({ value, onChange }) {
	const [show, setShow] = useState(false);
	if (show) return <JsonInput value={value} rows={6} onChange={onChange} />;
	return (
		<button className="yta-add" style={{ width: 'auto' }} onClick={() => setShow(true)}>
			<Icon name="eye" /> {value === undefined ? 'Boş' : 'Gizli içerik'}: göster / düzenle
		</button>
	);
}

const FILTERS = [
	{ key: 'all', value: 'all', text: 'Tüm alanlar' },
	{ key: 'set', value: 'set', text: 'Hesapta tanımlı olanlar' },
	{ key: 'changed', value: 'changed', text: 'Kaydedilmemişler' },
];

export default function SettingsTab({ tab, fields, getValue, isSet, isChanged, setValue }) {
	const [search, setSearch] = useState('');
	const [filter, setFilter] = useState('all');
	const [open, setOpen] = useState({});

	let q = norm(search.trim());
	let visible = _.filter(fields, (f) => {
		if (q && !fieldMatch(f, q, getValue(f.path))) return false;
		if (filter === 'set') return isSet(f.path);
		if (filter === 'changed') return isChanged(f.path);
		return true;
	});
	let sections = _.uniq(_.map(fields, 'section'));
	let setCount = _.filter(fields, (f) => isSet(f.path)).length;

	return (
		<div>
			{tab.desc ? <div className="yta-info">{tab.desc}</div> : null}
			<div className="yta-toolbar">
				<Input icon="search" placeholder="Alan ara (ad, anahtar, açıklama)" value={search} onChange={(e, d) => setSearch(d.value)} style={{ width: 340 }} />
				<Dropdown selection value={filter} options={FILTERS} onChange={(e, d) => setFilter(d.value)} />
				<div className="grow" />
				<span className="yta-muted">
					{fields.length} alan · {setCount} tanımlı
				</span>
			</div>
			{_.map(sections, (section) => {
				let list = _.filter(visible, { section });
				if (!list.length) return null;
				let sectionSet = _.filter(list, (f) => isSet(f.path)).length;
				let sectionChanged = _.filter(list, (f) => isChanged(f.path)).length;
				// varsayılan: arama/filtre varsa ya da bölümde tanımlı alan varsa açık
				let isOpen = open[section] !== undefined ? open[section] : !!(q || filter !== 'all' || sectionSet || sectionChanged || sections.length <= 2);
				return (
					<div key={section} className={'yta-card' + (isOpen ? '' : ' collapsed')}>
						<div className="yta-card-head collapsible" onClick={() => setOpen(_.assign({}, open, { [section]: !isOpen }))}>
							<Icon name={isOpen ? 'chevron down' : 'chevron right'} size="small" />
							{section}
							<span className="yta-count">{list.length}</span>
							{sectionSet ? <span className="yta-badge ok">{sectionSet} tanımlı</span> : null}
							{sectionChanged ? <span className="yta-badge accent">{sectionChanged} değişti</span> : null}
						</div>
						{isOpen ? (
							<div className="yta-card-body">
								{_.map(list, (f) => (
									<FieldRow key={f.path} field={f} value={getValue(f.path)} isSet={isSet(f.path)} changed={isChanged(f.path)} onChange={(v) => setValue(f.path, v)} />
								))}
							</div>
						) : null}
					</div>
				);
			})}
			{!visible.length ? <div className="yta-empty">Eşleşen alan yok.</div> : null}
		</div>
	);
}
